export const GT_STATUS_NOT_STARTED = "NOT_STARTED";
export const GT_STATUS_DRAFT = "DRAFT";
export const GT_STATUS_COMPLETED = "COMPLETED";

export const GT_STATUS_RANK = Object.freeze({
  [GT_STATUS_NOT_STARTED]: 0,
  [GT_STATUS_DRAFT]: 1,
  [GT_STATUS_COMPLETED]: 2
});

const toUpper = value => `${value || ""}`.trim().toUpperCase();

const isNonEmpty = value => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const sectionStudyValue = (meta, sectionKey) => {
  if (!meta || typeof meta !== "object") return "";
  const section = meta[sectionKey];
  if (!section || typeof section !== "object") return "";
  return section.study || "";
};

export const hasStudyDensity = meta =>
  isNonEmpty(sectionStudyValue(meta, "breastDensity"));

export const hasStudyDiagnosis = meta =>
  isNonEmpty(sectionStudyValue(meta, "diagnosis"));

const hasSectionSignal = (meta, key) =>
  ["study", "L", "R"].some(field => isNonEmpty(meta?.[key]?.[field]));

export const canMarkGtStudyCompleted = meta =>
  hasStudyDensity(meta) && hasStudyDiagnosis(meta);

export const resetStudyCompletionIfNeeded = meta => {
  const base = meta && typeof meta === "object" ? { ...meta } : {};
  if (!hasCompletedFlag(base)) {
    return base;
  }

  const nextValidation =
    base.validation && typeof base.validation === "object"
      ? { ...base.validation }
      : {};
  nextValidation.status = GT_STATUS_DRAFT;
  nextValidation.isCompleted = false;
  delete nextValidation.completedAt;
  delete nextValidation.completedBy;
  base.validation = nextValidation;
  return base;
};

export const hasCompletedFlag = meta => {
  if (!meta || typeof meta !== "object") return false;
  const validation = meta.validation;
  if (!validation || typeof validation !== "object") return false;
  if (validation.isCompleted === true) return true;
  return toUpper(validation.status) === GT_STATUS_COMPLETED;
};

export const normalizeGtStatusValue = value => {
  const normalized = toUpper(value);
  return GT_STATUS_RANK[normalized] === undefined ? "" : normalized;
};

export const computeGtStudyStatus = (
  meta,
  { hasMedicalReport = false, hasAnnotations = false } = {}
) => {
  const completedRequested = hasCompletedFlag(meta);

  if (completedRequested && canMarkGtStudyCompleted(meta)) {
    return GT_STATUS_COMPLETED;
  }

  const hasSignal =
    hasSectionSignal(meta, "breastDensity") ||
    hasSectionSignal(meta, "diagnosis") ||
    completedRequested ||
    toUpper(meta?.validation?.status) === GT_STATUS_DRAFT ||
    Boolean(hasMedicalReport) ||
    Boolean(hasAnnotations);

  if (hasSignal) {
    return GT_STATUS_DRAFT;
  }

  return GT_STATUS_NOT_STARTED;
};

export const applyGtWorkflowStatus = (
  meta,
  { hasMedicalReport = false, hasAnnotations = false } = {}
) => {
  const base = meta && typeof meta === "object" ? { ...meta } : {};
  const densitySection =
    base.breastDensity && typeof base.breastDensity === "object"
      ? { ...base.breastDensity }
      : {};
  const diagnosisSection =
    base.diagnosis && typeof base.diagnosis === "object"
      ? { ...base.diagnosis }
      : {};
  base.breastDensity = {
    study: densitySection.study || "",
    splitByLaterality: Boolean(densitySection.splitByLaterality),
    L: densitySection.L || "",
    R: densitySection.R || ""
  };
  base.diagnosis = {
    study: diagnosisSection.study || "",
    splitByLaterality: Boolean(diagnosisSection.splitByLaterality),
    L: diagnosisSection.L || "",
    R: diagnosisSection.R || ""
  };
  const nextValidation =
    base.validation && typeof base.validation === "object"
      ? { ...base.validation }
      : {};
  const status = computeGtStudyStatus(base, {
    hasMedicalReport,
    hasAnnotations
  });
  const isCompleted = status === GT_STATUS_COMPLETED;

  nextValidation.status = status;
  nextValidation.isCompleted = isCompleted;
  if (!isCompleted) {
    delete nextValidation.completedAt;
    delete nextValidation.completedBy;
  }

  base.validation = nextValidation;
  return base;
};
